import React from 'react';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, Clock, Users, User } from 'lucide-react';
import { EVENT_TYPE_META, fmtLong, eventsOnDate } from '@/constants/calendarMeta';

const Row = ({ ev, canEdit, onEdit, onDelete, showDate }) => {
  const meta = EVENT_TYPE_META[ev.type] || EVENT_TYPE_META.other;
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2.5" data-testid={`event-row-${ev.id}`}>
      <div className="flex items-start gap-2">
        <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: meta.color }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[hsl(var(--text-primary))] truncate" title={ev.title}>{ev.title}</p>
          <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[11px] text-[hsl(var(--text-muted))]">
            {showDate && <span>{fmtLong(ev.date)}{ev.end_date && ev.end_date !== ev.date ? ` → ${fmtLong(ev.end_date)}` : ''}</span>}
            {ev.start_time && <span className="inline-flex items-center gap-1"><Clock size={10} />{ev.start_time}</span>}
            <span className={`px-1.5 rounded ${meta.bg} ${meta.text}`}>{meta.label}</span>
            <span className="inline-flex items-center gap-1">
              {ev.scope === 'team' ? <><Users size={10} /> Team</> : <><User size={10} /> Private</>}
            </span>
            <span>by {ev.created_by}</span>
          </div>
          {ev.notes && <p className="text-xs text-[hsl(var(--text-muted))] mt-1.5 whitespace-pre-wrap">{ev.notes}</p>}
        </div>
        {canEdit(ev) && (
          <div className="flex gap-1 shrink-0">
            <button onClick={() => onEdit(ev)} className="p-1.5 rounded hover:bg-white/10 text-[hsl(var(--text-muted))] hover:text-blue-400"
              data-testid={`event-edit-${ev.id}`}><Pencil size={13} /></button>
            <button onClick={() => onDelete(ev)} className="p-1.5 rounded hover:bg-white/10 text-[hsl(var(--text-muted))] hover:text-red-400"
              data-testid={`event-delete-${ev.id}`}><Trash2 size={13} /></button>
          </div>
        )}
      </div>
    </div>
  );
};

export const DayPanel = ({ date, events, canEdit, onEdit, onDelete, onAdd }) => {
  const list = eventsOnDate(events, date);
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3.5" data-testid="calendar-day-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Selected day</p>
          <p className="text-sm font-semibold text-[hsl(var(--text-primary))]">{fmtLong(date)}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => onAdd(date)} data-testid="day-add-event-btn">Add</Button>
      </div>
      <div className="space-y-2" data-testid="day-event-list">
        {list.length === 0 && <p className="text-xs text-[hsl(var(--text-muted))]">No events on this day.</p>}
        {list.map((ev) => (
          <Row key={ev.id} ev={ev} canEdit={canEdit} onEdit={onEdit} onDelete={onDelete} showDate={false} />
        ))}
      </div>
    </div>
  );
};

export const AgendaList = ({ events, canEdit, onEdit, onDelete }) => (
  <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3.5" data-testid="calendar-agenda">
    <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--text-muted))] mb-3">Upcoming (next 30 days)</p>
    <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
      {events.length === 0 && <p className="text-xs text-[hsl(var(--text-muted))]">Nothing scheduled yet.</p>}
      {events.map((ev) => (
        <Row key={ev.id} ev={ev} canEdit={canEdit} onEdit={onEdit} onDelete={onDelete} showDate />
      ))}
    </div>
  </div>
);
